import {
  Box, Button, Card, CardContent, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, LinearProgress,
  Stack, TextField, Typography, Alert, Paper
} from "@mui/material";
import { Add, Edit, Delete, Close, Link as LinkIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const QuickLinkManagement = () => {
  const { isLoading, error, submitData } = usePostData();
  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("quick-links");

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [quickLinks, setQuickLinks] = useState([]);

  const [formData, setFormData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    email: "",
    phone: "",
    address_en: "",
    address_fr: "",
    box: "",
  });

  useEffect(() => {
    if (data?.data?.data) {
      setQuickLinks(data.data.data);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditMode(true);
      setCurrentItem(item);
      setFormData(item);
    } else {
      setEditMode(false);
      setFormData({
        name_en: "", name_fr: "", description_en: "", description_fr: "",
        email: "", phone: "", address_en: "", address_fr: "", box: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrorMessage("");
    setCurrentItem(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name_en && !formData.name_fr) {
      setErrorMessage("Le nom est obligatoire (EN ou FR).");
      return;
    }

    const endpoint = editMode
      ? `quick-links/${currentItem.id}`
      : "quick-links";

    const method = editMode ? "PUT" : "POST";
    submitData(endpoint, formData, method);
    refreshSearch();
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm("Supprimer ce lien ?")) {
      submitData(`quick-links/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress />}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinkIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Quick Links
          </Typography>
        </Stack>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Nouveau lien
        </Button>
      </Stack>

      {error && <Alert severity="error">{error?.message}</Alert>}
      {loading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {Array.isArray(quickLinks) && quickLinks.length > 0 ? (
          quickLinks.map((item, i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{item.name_en || item.name_fr}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description_en || item.description_fr}
                  </Typography>
                  {item.email && <Typography variant="caption">📧 {item.email}</Typography>}
                  {item.phone && <Typography variant="caption">📞 {item.phone}</Typography>}
                </CardContent>
                <Stack direction="row" justifyContent="flex-end" p={2}>
                  <IconButton color="primary" onClick={() => handleOpenDialog(item)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(item.id)}>
                    <Delete />
                  </IconButton>
                </Stack>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center" }}>Aucun Quick Link</Paper>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between">
            <Typography>{editMode ? "Modifier le lien" : "Nouveau lien"}</Typography>
            <IconButton onClick={handleCloseDialog}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Stack spacing={2} mt={1}>
            <TextField label="Nom (EN)" name="name_en" value={formData.name_en} onChange={handleInputChange} />
            <TextField label="Nom (FR)" name="name_fr" value={formData.name_fr} onChange={handleInputChange} />
            <TextField label="Description (EN)" name="description_en" value={formData.description_en} onChange={handleInputChange} />
            <TextField label="Description (FR)" name="description_fr" value={formData.description_fr} onChange={handleInputChange} />
            <TextField label="Email" name="email" value={formData.email} onChange={handleInputChange} />
            <TextField label="Téléphone" name="phone" value={formData.phone} onChange={handleInputChange} />
            <TextField label="Adresse (EN)" name="address_en" value={formData.address_en} onChange={handleInputChange} />
            <TextField label="Adresse (FR)" name="address_fr" value={formData.address_fr} onChange={handleInputChange} />
            <TextField label="URL " name="box" value={formData.box} onChange={handleInputChange} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">{editMode ? "Mettre à jour" : "Créer"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuickLinkManagement;
